import { askAI } from '../ai/client';
import { buildClarifierPrompt, type CombinedReading, parseCombinedResponse } from '../ai/prompts';
import { cards as ALL_CARDS } from '../data/cards';
import { tEn, tZh } from '../i18n';
import type {
  Card,
  DisposableScreen,
  LinyuanNavDetail,
  Position,
  ReadingContext,
  ReadingState,
} from '../types';
import { hideColorTooltip } from './color-tooltip';
import { escapeHtml } from './escape';
import {
  createReadingBlock,
  markAIFailed,
  readUseCardImagesPref,
  setAIText,
} from './reading-block';
import {
  applySynthesis,
  mountSynthesisFooter,
  refreshClusterBanners,
  setSynthesisLoading,
} from './synthesis';

export const MAX_CLARIFIERS = 3;

/**
 * Mounts the reveal screen. The AI request and abort controller must be
 * provided by the caller; mountReveal just consumes the cached promise and
 * wires the dispose path to the AC.
 */
export function mountReveal(
  ctx: ReadingContext,
  ac: AbortController,
): { element: HTMLElement; state: ReadingState } {
  const state: ReadingState = {
    spread: { ...ctx.spread, positions: [...ctx.spread.positions] },
    cards: ctx.cards,
    question: ctx.question,
    isGeneral: ctx.isGeneral,
    flippedCardIds: new Set(),
    pendingTimers: [],
    cachedReading: ctx.cachedReading,
    clarifierAI: new Map(),
    clarifierACs: [],
    cardTexts: new Map(),
    useCardImages: readUseCardImagesPref(),
  };

  // Populate cardTexts from the original reading once it resolves so the
  // clarifier prompt builder has every prior card's already-spoken text
  // available synchronously (no awaiting in-flight promises).
  ctx.cachedReading.then(
    (reading) => {
      for (const e of reading.cards) {
        state.cardTexts?.set(e.id, { text_en: e.text_en, text_zh: e.text_zh });
      }
    },
    () => {
      /* original AI failed — clarifier prompt sees empty text for originals */
    },
  );

  const screen = document.createElement('section');
  screen.id = 'screen-reveal';
  screen.className = 'screen active';
  screen.innerHTML = `
    <div class="reveal-title">
      <span lang="en"><em>${tEn('reveal.drawing')}</em></span>
      <span lang="zh">${tZh('reveal.drawing')}</span>
    </div>
    <div class="deal-stage layout-${ctx.spread.id}" id="deal-stage"></div>
    <div class="reveal-hint">
      <span lang="en">${tEn('reveal.hint')}</span>
      <span lang="zh">${tZh('reveal.hint')}</span>
    </div>
    <div class="reading-panel" id="reading-panel"></div>
    <div class="reading-footer" id="reading-footer" style="display:none"></div>
  `;

  const stage = screen.querySelector('#deal-stage') as HTMLElement;
  const panel = screen.querySelector('#reading-panel') as HTMLElement;

  ctx.cards.forEach((c: Card, i: number) => {
    const el = document.createElement('div');
    el.className = 'deal-card';
    el.dataset.cardId = String(c.id);
    el.dataset.cardIndex = String(i);
    el.innerHTML = cardFaceHtml(c);
    stage.appendChild(el);
    state.pendingTimers?.push(setTimeout(() => el.classList.add('in'), 200 + i * 380));
    attachFlipHandler(el, c, ctx.spread.positions[i] as Position, panel, screen, state);
  });

  (screen as DisposableScreen).dispose = () => {
    ac.abort();
    if (state.clarifierACs) {
      for (const a of state.clarifierACs) a.abort();
      state.clarifierACs.length = 0;
    }
    if (state.pendingTimers) {
      for (const t of state.pendingTimers) clearTimeout(t);
      state.pendingTimers.length = 0;
    }
    if (state.capTooltip) {
      clearTimeout(state.capTooltip.timer);
      document.removeEventListener('click', state.capTooltip.listener);
      state.capTooltip = undefined;
    }
    // The color tooltip lives on document.body and would otherwise linger
    // visually on the next screen until the user's next click.
    hideColorTooltip();
  };

  return { element: screen, state };
}

/**
 * Wires the flip click handler onto a .deal-card element.
 * Shared by the initial deal and clarifier-added cards.
 */
function attachFlipHandler(
  el: HTMLElement,
  card: Card,
  position: Position,
  panel: HTMLElement,
  screen: HTMLElement,
  state: ReadingState,
): void {
  el.addEventListener('click', () => {
    if (el.classList.contains('flipped')) return;
    // Don't flip until the card has slid into place — prevents a rapid
    // post-breath-skip tap from registering on a card mid-deal.
    if (!el.classList.contains('in')) return;
    el.classList.add('flipped');
    state.flippedCardIds.add(card.id);
    // Clarifier flips: refresh cluster banners now that the new card is
    // face-up. Mounted footer means the synthesis is showing, which means
    // the original draw is fully revealed — only clarifier flips reach
    // this branch. Original-deal flips skip it; banners will render once
    // mountSynthesisFooter runs after the last original card flips.
    if (state.synthesisMounted) {
      const footerEl = screen.querySelector<HTMLElement>('#reading-footer');
      if (footerEl) refreshClusterBanners(footerEl, state);
    }
    const block = createReadingBlock(card, position, true, state.useCardImages);
    panel.appendChild(block);
    block.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Clarifier cards have their own AI promise; original cards read from
    // the shared cachedReading. Both shapes are CombinedReading — the find
    // by card.id works on either (clarifier responses contain just one card).
    const aiPromise = state.clarifierAI?.get(card.id) ?? state.cachedReading;
    aiPromise?.then(
      (reading) => {
        const entry = reading.cards.find((c) => c.id === card.id);
        if (entry) setAIText(block, entry.text_en, entry.text_zh);
        else markAIFailed(block);
      },
      () => markAIFailed(block),
    );

    if (state.flippedCardIds.size === state.cards.length && !state.synthesisMounted) {
      state.synthesisMounted = true;
      // Swap the screen title now that the deal phase is over.
      const titleEl = screen.querySelector<HTMLElement>('.reveal-title');
      if (titleEl) {
        titleEl.innerHTML = `<span lang="en"><em>${tEn('reveal.complete')}</em></span><span lang="zh">${tZh('reveal.complete')}</span>`;
      }
      // Defer to next frame so the per-card scrollIntoView completes before the
      // synthesis footer's own scrollIntoView fires (avoids visual scroll-fight
      // on 1-card spreads where both happen on the same flip).
      requestAnimationFrame(() => {
        const footer = screen.querySelector('#reading-footer') as HTMLElement;
        mountSynthesisFooter(footer, state, {
          onDrawAgain: () =>
            window.dispatchEvent(
              new CustomEvent<LinyuanNavDetail>('linyuan:nav', {
                // state.spread.positions is mutated when clarifier cards are
                // pulled (an entry pushed per clarifier). Send a clean copy
                // truncated to the original count so the next reading isn't
                // built against a spread bloated with stale clarifier slots.
                detail: {
                  to: 'reveal-reset',
                  spread: {
                    ...state.spread,
                    positions: state.spread.positions.slice(0, state.spread.count),
                  },
                },
              }),
            ),
          onClarify: () => pullClarifier(screen, state),
          onDone: () =>
            window.dispatchEvent(
              new CustomEvent<LinyuanNavDetail>('linyuan:nav', { detail: { to: 'home' } }),
            ),
        });
      });
    }
  });
}

/**
 * Pulls one additional card not already in the reading, appends it, and
 * fires a clarifier AI request that returns:
 *   - new card text (1 entry in CombinedReading.cards)
 *   - updated synthesis + reflection that integrates the full spread
 *
 * Original cards' AI text is passed in as immutable context — the model
 * is instructed not to rewrite it. Multiple rapid clarifier clicks are
 * sequence-tagged so only the latest clarifier's synthesis is applied,
 * but each card's text is applied independently.
 */
function pullClarifier(screen: HTMLElement, state: ReadingState): void {
  // Guard against rapid double-clicks during the 80ms slide-in.
  if (state.flippedCardIds.size < state.cards.length) return;
  // Hard cap on clarifiers per session. Click on the greyed button still
  // routes here — surface the tooltip so the user gets feedback.
  if ((state.clarifierAI?.size ?? 0) >= MAX_CLARIFIERS) {
    showCapMessage(screen, state);
    return;
  }

  const drawn = new Set(state.cards.map((c) => c.id));
  const remaining = ALL_CARDS.filter((c) => !drawn.has(c.id));
  if (remaining.length === 0) return; // all 45 cards drawn — edge case
  const next = remaining[Math.floor(Math.random() * remaining.length)] as Card;

  // Snapshot of cards present BEFORE this clarifier — fed to the prompt.
  const prevCards = state.cards.slice();
  const prevPositions = state.spread.positions.slice();

  state.cards.push(next);
  const clarifierPosition: Position = {
    label_en: tEn('position.clarifier'),
    label_zh: tZh('position.clarifier'),
  };
  state.spread.positions.push(clarifierPosition);

  const stage = screen.querySelector('#deal-stage') as HTMLElement;
  const panel = screen.querySelector('#reading-panel') as HTMLElement;
  const footer = screen.querySelector('#reading-footer') as HTMLElement;

  const el = document.createElement('div');
  el.className = 'deal-card';
  el.dataset.cardId = String(next.id);
  el.dataset.cardIndex = String(state.cards.length - 1);
  el.innerHTML = cardFaceHtml(next);
  stage.appendChild(el);
  state.pendingTimers?.push(setTimeout(() => el.classList.add('in'), 80));
  // On mobile, wide spreads scroll horizontally — bring the new card into
  // view so the user sees it appear instead of it sliding in off-screen.
  state.pendingTimers?.push(
    setTimeout(
      () => el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' }),
      120,
    ),
  );

  attachFlipHandler(el, next, clarifierPosition, panel, screen, state);

  // Fire the clarifier AI request in parallel with the slide-in.
  const ac = new AbortController();
  state.clarifierACs?.push(ac);

  setSynthesisLoading(footer, true, /* weaving */ true);
  // Cluster banners are NOT refreshed here — that would spoil the
  // clarifier's color before the user flips it. The flip handler in
  // attachFlipHandler refreshes once the card lands face-up.

  // Sync prep — no awaiting prior in-flight clarifiers; cardTexts is
  // populated as each promise resolves.
  const knownCards = collectKnownCards(state, prevCards, prevPositions);
  const aiPromise: Promise<CombinedReading> = askAI(
    buildClarifierPrompt({
      spread: state.spread,
      question: state.question,
      isGeneral: state.isGeneral,
      knownCards,
      newCard: next,
      newPosition: clarifierPosition,
    }),
    ac.signal,
  ).then((r) => parseCombinedResponse(r.text));

  // Suppress unhandled-rejection — flip handler + apply branch each have their own catch.
  aiPromise.catch(() => {
    /* handled downstream */
  });

  state.clarifierAI?.set(next.id, aiPromise);
  // Capture the sequence number AFTER the set so size === my sequence.
  const mySeq = state.clarifierAI?.size ?? 0;

  // Populate cardTexts on resolve so a subsequent clarifier prompt sees
  // this clarifier's already-spoken text.
  aiPromise.then(
    (reading) => {
      for (const e of reading.cards) {
        state.cardTexts?.set(e.id, { text_en: e.text_en, text_zh: e.text_zh });
      }
    },
    () => {
      /* clarifier failed — empty text contributed if asked again */
    },
  );

  // Grey out the Clarify button once the cap is reached.
  if ((state.clarifierAI?.size ?? 0) >= MAX_CLARIFIERS) {
    const btn = footer.querySelector<HTMLButtonElement>('#act-clarify');
    if (btn) {
      btn.classList.add('capped');
      btn.dataset.capEn = tEn('actions.clarify_cap');
      btn.dataset.capZh = tZh('actions.clarify_cap');
    }
  }
  updateCapCounter(footer, state);

  // Synthesis update — gated on sequence so older requests don't overwrite.
  // Both arms gated symmetrically (BUG-001 fix).
  aiPromise.then(
    (reading) => {
      if (mySeq !== state.clarifierAI?.size) return;
      setSynthesisLoading(footer, false);
      // applySynthesis writes to innerHTML and calls escapeHtml on each
      // synthesis/reflection field — escapeHtml(undefined) throws. If
      // model output drift produces a reading with a missing field,
      // surface the failure indicator instead of leaking an unhandled
      // rejection that leaves the loading ripple stuck on screen.
      try {
        applySynthesis(footer, reading);
      } catch (e) {
        console.warn('clarifier applySynthesis failed:', (e as Error).message);
        const stateEl = footer.querySelector<HTMLElement>('#ai-state');
        if (stateEl) {
          stateEl.innerHTML = `<span lang="en">${tEn('ai.state_static')}</span><span lang="zh">${tZh('ai.state_static')}</span>`;
        }
      }
    },
    () => {
      if (mySeq !== state.clarifierAI?.size) return;
      setSynthesisLoading(footer, false);
      // Don't overwrite the #ai-state line on clarifier failure — the
      // prior synthesis was AI-composed and that label ('Reading composed
      // by 深海') still tells the truth about the visible reading. The
      // user sees the original reading remain intact; the clarifier just
      // didn't get integrated. The loading ripple coming off is enough
      // signal that the update didn't land.
    },
  );
}

/**
 * Synchronously gather already-spoken AI text for each card. Reads from
 * `state.cardTexts` which is populated by `.then` handlers on cachedReading
 * and each clarifier promise. In-flight requests contribute empty text;
 * the prompt builder skips the "already spoken" line when both langs empty.
 */
function collectKnownCards(
  state: ReadingState,
  cards: Card[],
  positions: Position[],
): { card: Card; position: Position; text_en: string; text_zh: string }[] {
  return cards.map((c, i) => {
    const t = state.cardTexts?.get(c.id);
    return {
      card: c,
      position: positions[i] as Position,
      text_en: t?.text_en ?? '',
      text_zh: t?.text_zh ?? '',
    };
  });
}

/** Update the 0/3 counter shown on the Clarify button. */
function updateCapCounter(footer: HTMLElement, state: ReadingState): void {
  const counter = footer.querySelector<HTMLElement>('#cap-count');
  if (counter) counter.textContent = `${state.clarifierAI?.size ?? 0}/${MAX_CLARIFIERS}`;
}

/**
 * Surface the "勿扰既定命运" tooltip on the capped Clarify button.
 * Single shared lifecycle handle (state.capTooltip) — repeated taps refresh
 * the timer and replace the listener instead of stacking. Dispose tears it
 * down so the document listener doesn't outlive the screen.
 */
function showCapMessage(screen: HTMLElement, state: ReadingState): void {
  const btn = screen.querySelector<HTMLElement>('#act-clarify');
  if (!btn) return;
  btn.classList.add('show-cap-msg');

  // Tear down any prior tooltip handle so listeners don't pile up.
  if (state.capTooltip) {
    clearTimeout(state.capTooltip.timer);
    document.removeEventListener('click', state.capTooltip.listener);
  }

  const dismiss = (): void => {
    btn.classList.remove('show-cap-msg');
    if (state.capTooltip) {
      clearTimeout(state.capTooltip.timer);
      document.removeEventListener('click', state.capTooltip.listener);
      state.capTooltip = undefined;
    }
  };

  state.capTooltip = {
    timer: setTimeout(dismiss, 2500),
    listener: dismiss,
  };
  // Defer the click listener registration so the click that triggered this
  // call doesn't immediately dismiss it.
  setTimeout(() => {
    if (state.capTooltip) document.addEventListener('click', state.capTooltip.listener);
  }, 0);
}

function cardFaceHtml(c: Card): string {
  const color = /^#[0-9a-fA-F]{3,8}$/.test(c.color) ? c.color : '#888';
  return `
    <div class="face back"></div>
    <div class="face front" style="border-color:${color};box-shadow:0 0 18px ${color}33, inset 0 0 30px ${color}1a;">
      <div class="c-id">${String(c.id).padStart(2, '0')}</div>
      <div class="c-glyph" style="color:${color};text-shadow:0 0 12px ${color}80;">${escapeHtml(c.glyph)}</div>
      <div class="c-name"><span lang="en">${escapeHtml(c.name_en)}</span><span lang="zh">${escapeHtml(c.name_zh)}</span></div>
    </div>`;
}
